/* eslint-disable react/prop-types */
"use client";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import Chat from "../../pages/menu/Chat";
import DirectChat from "../../pages/menu/DirectChat";
import { AUTH_GRADIENT_STYLE } from "../../constants/authVisuals";
import ChatsModal from "./ChatsModal";
import ProfessionalMeetupInviteChat from "./ProfessionalMeetupInviteChat";
import { isDeletedAccountUser } from "../../utils/deletedAccount";
import useNativeSwipeBack from "../../hooks/useNativeSwipeBack";
import {
  CHAT_LIST_PATH,
  buildChatListNavigationOptions,
  getChatListTabForKind,
} from "../../utils/chatNavigation";

const CHAT_PANEL_COLOR = "#0D1428";
const CHAT_PRIMARY_BLUE = "#3E6FFB";
const DIRECT_PROFILE_CAPSULE_SHAPE_CLASS =
  "rounded-bl-[67px] rounded-br-[24px] rounded-tl-[67px] rounded-tr-[24px]";
const CHAT_MOTION_EASE = [0.22, 1, 0.36, 1];
const CHAT_ROUTE_TRANSITION_MS = 280;

function isRetryableChatRequestError(error) {
  const status = Number(error?.response?.status || 0);
  return !status || status === 408 || status === 425 || status === 429 || status >= 500;
}

async function getChatResource(url, config) {
  try {
    return await api.get(url, config);
  } catch (error) {
    if (!isRetryableChatRequestError(error)) throw error;
    return api.get(url, config);
  }
}

const chatRouteVariants = {
  initial: {
    opacity: 0,
    y: 12,
    scale: 0.988,
    filter: "blur(10px)",
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: {
      duration: 0.32,
      ease: CHAT_MOTION_EASE,
    },
  },
  exit: {
    opacity: 0,
    y: 16,
    scale: 0.988,
    filter: "blur(10px)",
    transition: {
      duration: 0.22,
      ease: CHAT_MOTION_EASE,
    },
  },
  leaving: {
    opacity: 0,
    y: 22,
    scale: 0.982,
    filter: "blur(8px)",
    transition: {
      duration: CHAT_ROUTE_TRANSITION_MS / 1000,
      ease: CHAT_MOTION_EASE,
    },
  },
};

const chatContentVariants = {
  initial: {
    opacity: 0,
    scale: 0.998,
    filter: "blur(6px)",
  },
  animate: {
    opacity: 1,
    scale: 1,
    filter: "blur(0px)",
    transition: {
      duration: 0.24,
      ease: CHAT_MOTION_EASE,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.998,
    filter: "blur(6px)",
    transition: {
      duration: 0.16,
      ease: CHAT_MOTION_EASE,
    },
  },
};

const loadingInputT = (key, options) => {
  const defaultValue = options?.defaultValue;
  if (defaultValue) return defaultValue;
  if (key === "chats.event_chat.input_placeholder") return "Message";
  if (key === "chats.event_chat.attach_aria") return "Attach";
  if (key === "chats.event_chat.send_aria") return "Send";
  if (key === "chats.event_chat.emoji_aria") return "Emoji";
  if (key === "chats.event_chat.cancel_context") return "Cancel";
  return key;
};

function normalizeEventDetailsPayload(payload) {
  if (!payload || typeof payload !== "object") return {};
  const nested = payload.event && typeof payload.event === "object" ? payload.event : null;
  return nested || payload;
}

function SkeletonBlock({ className = "", dark = false, ...props }) {
  return (
    <span
      {...props}
      className={`vg-chat-loading-shimmer block rounded-full ${
        dark ? "bg-black/10" : "bg-white/16"
      } ${className}`}
      aria-hidden="true"
    />
  );
}

function LoadingBackButton({
  onBack,
  isDesktop = false,
  topClassName = "top-[calc(109px+var(--vg-safe-area-top))]",
}) {
  return (
    <button
      type="button"
      onClick={onBack}
      className={`absolute ${isDesktop ? "left-[34px] h-8 w-8" : "left-[26px] h-6 w-6"} ${topClassName} flex items-center justify-center text-white transition active:scale-[0.98]`}
      aria-label="Back"
    >
      <svg
        width={isDesktop ? 20 : 24}
        height={isDesktop ? 20 : 24}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M15 18L9 12L15 6"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function EventHeaderSkeleton({ onBack, kind, isDesktop = false }) {
  const isGroup = kind === "group";
  const headerPositionClass = isDesktop
    ? "left-[42px] right-[42px] top-[11px]"
    : isGroup
    ? "left-[30px] right-[30px] top-[calc(11px+var(--vg-safe-area-top))]"
    : "left-[24px] right-[24px] top-[calc(11px+var(--vg-safe-area-top))]";
  const headerShapeClass = isGroup
    ? `flex-col items-center justify-center rounded-[67px] text-center ${
        isDesktop ? "px-8" : "px-6"
      }`
    : "items-center gap-[10px] rounded-[16px] px-[10px] text-left";
  const headerHeightClass = isDesktop
    ? "h-[138px]"
    : "h-[calc(138px+var(--vg-safe-area-top))]";
  const participantsTopClass = isDesktop
    ? "top-[103px]"
    : "top-[calc(103px+var(--vg-safe-area-top))]";
  const participantsWidthClass = isDesktop ? "w-[240px]" : "w-[217px]";

  return (
    <div className={`relative z-20 ${headerHeightClass} shrink-0 overflow-visible`}>
      <LoadingBackButton
        onBack={onBack}
        isDesktop={isDesktop}
        topClassName={isDesktop ? "top-[109px]" : undefined}
      />
      <div
        data-testid="event-header-skeleton"
        className={`absolute flex h-20 bg-white shadow-[0_10px_22px_rgba(0,0,0,0.08)] ${headerPositionClass} ${headerShapeClass}`}
      >
        {isGroup ? (
          <SkeletonBlock dark className="h-[24px] w-[74%]" />
        ) : (
          <>
            <SkeletonBlock
              dark
              data-testid="event-avatar-skeleton"
              className="h-[60px] w-[60px] shrink-0 rounded-[14px]"
            />
            <div className="min-w-0 flex-1">
              <SkeletonBlock dark className="h-[24px] w-[74%]" />
              <SkeletonBlock dark className="mt-3 h-[14px] w-[44%]" />
            </div>
          </>
        )}
      </div>
      <div
        data-testid="participants-pill-skeleton"
        className={`absolute left-1/2 ${participantsTopClass} flex h-[35px] ${participantsWidthClass} -translate-x-1/2 items-center justify-center rounded-[67px] px-5`}
        style={{ backgroundColor: CHAT_PRIMARY_BLUE }}
      >
        <SkeletonBlock className="h-[13px] w-[70%] bg-white/36" />
      </div>
    </div>
  );
}

function DirectHeaderSkeleton({ onBack, isDesktop = false }) {
  const headerHeightClass = isDesktop
    ? "h-[194px]"
    : "h-[calc(194px+var(--vg-safe-area-top))]";
  const headerPositionClass = isDesktop
    ? "left-[42px] right-[42px] top-[11px]"
    : "left-[30px] right-[30px] top-[calc(11px+var(--vg-safe-area-top))]";
  const participantsTopClass = isDesktop
    ? "top-[103px]"
    : "top-[calc(103px+var(--vg-safe-area-top))]";
  const participantsWidthClass = isDesktop ? "w-[240px]" : "w-[217px]";

  return (
    <div className={`relative z-20 ${headerHeightClass} shrink-0 overflow-visible`}>
      <div
        data-testid="direct-header-skeleton"
        className={`absolute ${headerPositionClass} flex h-20 items-center justify-between overflow-hidden ${DIRECT_PROFILE_CAPSULE_SHAPE_CLASS} bg-white px-[10px] py-[10px] shadow-[0_14px_28px_rgba(0,0,0,0.16)]`}
      >
        <div className="flex min-w-0 flex-1 items-center gap-[10px]">
          <SkeletonBlock dark className="h-[60px] w-[60px] shrink-0 rounded-full" />
          <div className="min-w-0 flex-1">
            <SkeletonBlock dark className="h-[22px] w-[72px] rounded-md" />
            <SkeletonBlock dark className="mt-2 h-[14px] w-[116px]" />
          </div>
        </div>
        <div className="w-[62px] shrink-0">
          <div className="space-y-2">
            <SkeletonBlock dark className="ml-auto h-[14px] w-[48px]" />
            <SkeletonBlock dark className="ml-auto h-[14px] w-[58px]" />
          </div>
        </div>
      </div>
      <LoadingBackButton
        onBack={onBack}
        isDesktop={isDesktop}
        topClassName={
          isDesktop
            ? "top-[111px]"
            : "top-[calc(111px+var(--vg-safe-area-top))]"
        }
      />
      <div
        data-testid="direct-status-pill-skeleton"
        className={`absolute left-1/2 ${participantsTopClass} z-30 flex h-[35px] ${participantsWidthClass} -translate-x-1/2 items-center justify-center rounded-[67px] px-5`}
        style={{ backgroundColor: CHAT_PRIMARY_BLUE }}
      >
        <SkeletonBlock className="h-[13px] w-[62%] bg-white/36" />
      </div>
    </div>
  );
}

function LoadingMessageRow({ side = "left", delay = 0, compact = false }) {
  const isRight = side === "right";
  return (
    <div
      className={`vg-chat-loading-row flex items-end gap-3 ${
        isRight ? "justify-end pl-12" : "justify-start pr-8"
      }`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {!isRight ? (
        <div className="flex w-[58px] shrink-0 flex-col items-center gap-2">
          <SkeletonBlock className="h-[46px] w-[46px] rounded-full bg-white/13" />
          <SkeletonBlock className="h-[13px] w-[38px] bg-white/18" />
        </div>
      ) : null}
      <div
        className={`rounded-[28px] px-5 py-4 ${
          isRight
            ? "w-[76%] bg-white/92"
            : "w-[78%] bg-[#7396F7]"
        } ${compact ? "max-w-[265px]" : "max-w-[315px]"}`}
      >
        <div className="flex items-center justify-between gap-4">
          <SkeletonBlock
            dark={isRight}
            className={`h-[14px] ${compact ? "w-[44%]" : "w-[38%]"} ${
              isRight ? "bg-black/12" : "bg-white/42"
            }`}
          />
          <SkeletonBlock
            dark={isRight}
            className={`h-[12px] w-[44px] ${isRight ? "bg-black/10" : "bg-white/30"}`}
          />
        </div>
        <SkeletonBlock
          dark={isRight}
          className={`mt-4 h-[14px] w-[94%] ${isRight ? "bg-black/10" : "bg-white/34"}`}
        />
        {!compact ? (
          <SkeletonBlock
            dark={isRight}
            className={`mt-2 h-[14px] w-[68%] ${isRight ? "bg-black/10" : "bg-white/34"}`}
          />
        ) : null}
      </div>
      {isRight ? (
        <div className="flex w-[58px] shrink-0 flex-col items-center gap-2">
          <SkeletonBlock className="h-[46px] w-[46px] rounded-full bg-white/13" />
          <SkeletonBlock className="h-[13px] w-[34px] bg-white/18" />
        </div>
      ) : null}
    </div>
  );
}

function LoadingFeedSkeleton({ isDesktop }) {
  return (
    <div
      className={`relative z-0 min-h-0 flex-1 overflow-hidden px-4 ${
        isDesktop ? "pb-6 pt-8" : "pb-[calc(var(--bottom-nav-clearance,112px)+18px)] pt-11"
      }`}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-[-12px] z-10 h-[96px]"
        style={{
          background:
            "linear-gradient(180deg,#0D1428 0%,rgba(13,20,40,0.96) 24%,rgba(13,20,40,0.62) 58%,rgba(13,20,40,0) 100%)",
        }}
        aria-hidden="true"
      />
      <div className="relative z-0 flex flex-col gap-7">
        <LoadingMessageRow delay={70} />
        <LoadingMessageRow side="right" delay={145} compact />
        <LoadingMessageRow delay={220} compact />
        <div className="flex justify-center py-1">
          <SkeletonBlock className="h-[18px] w-[88px] bg-white/18" />
        </div>
        <LoadingMessageRow delay={295} />
        <LoadingMessageRow side="right" delay={370} />
      </div>
    </div>
  );
}

function DesktopInputSkeleton() {
  return (
    <div className="px-5 pb-6">
      <div
        className="mx-auto flex h-[70px] items-center rounded-[100px] border border-white/25 bg-[rgba(128,128,128,0.1)] px-5 shadow-[0_12px_30px_rgba(0,0,0,0.16)]"
        style={{ width: "min(816px, calc(100% - 84px))" }}
      >
        <SkeletonBlock className="h-6 w-6 rounded-md bg-white/22" />
        <SkeletonBlock className="ml-5 h-[16px] flex-1 bg-white/16" />
        <SkeletonBlock className="ml-4 h-[22px] w-[22px] rounded-full bg-white/16" />
        <SkeletonBlock className="ml-4 h-[46px] w-[46px] rounded-full bg-[#3E6FFB]/75" />
      </div>
    </div>
  );
}

function ChatLoadingShell({ kind, isDesktop, onBack }) {
  const isDirect = kind === "direct";
  if (!isDesktop) {
    return (
      <div
        className="relative flex h-full min-h-0 flex-col overflow-hidden"
        style={AUTH_GRADIENT_STYLE}
        data-testid="chat-loading-shell"
      >
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 top-[calc(51px+var(--vg-safe-area-top))] z-0 rounded-t-[45px] shadow-[0_-5px_25px_rgba(0,0,0,0.25)]"
          style={{ backgroundColor: CHAT_PANEL_COLOR }}
          aria-hidden="true"
        />
        {isDirect ? (
          <DirectHeaderSkeleton onBack={onBack} />
        ) : (
          <EventHeaderSkeleton onBack={onBack} kind={kind} />
        )}
        <LoadingFeedSkeleton isDesktop={false} />
      </div>
    );
  }

  return (
    <div
      className="relative flex h-full min-h-0 items-center justify-center bg-transparent px-6 py-6"
      data-testid="chat-loading-shell"
    >
      <div
        className="relative h-full w-full max-w-[900px] overflow-hidden"
        style={{ maxHeight: "min(874px, calc(100vh - 48px))" }}
      >
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 top-[51px] z-0 rounded-[45px]"
        style={{ backgroundColor: CHAT_PANEL_COLOR }}
        aria-hidden="true"
      />
        <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
          {isDirect ? (
            <DirectHeaderSkeleton onBack={onBack} isDesktop />
          ) : (
            <EventHeaderSkeleton onBack={onBack} kind={kind} isDesktop />
          )}
          <LoadingFeedSkeleton isDesktop />
          <DesktopInputSkeleton />
        </div>
      </div>
    </div>
  );
}

export function resolveReturnToChatsState({ kind, location }) {
  const source = String(location?.state?.source || "").trim().toLowerCase();
  const returnState = location?.state?.returnToChats || null;
  const preferredTab = String(returnState?.tab || "").trim().toLowerCase();
  const normalizedTab =
    preferredTab === "all" ||
    preferredTab === "groups" ||
    preferredTab === "direct" ||
    preferredTab === "events"
      ? preferredTab
      : null;
  const scrollTop = Number(returnState?.scrollTop);
  const normalizedScrollTop =
    Number.isFinite(scrollTop) && scrollTop >= 0 ? scrollTop : null;

  return {
    showBackdrop: source === "chats_modal",
    tab: normalizedTab || getChatListTabForKind(kind),
    scrollTop: normalizedScrollTop,
  };
}

/**
 * ChatOpen
 *
 * Single entry-point for opening chats via URL routes:
 *  - /menu/chats/event/:eventId
 *  - /menu/chats/direct/:openBy/:value   (openBy: "code" | "id")
 *  - /menu/chats/group/:groupChatId
 *  - /menu/chats/support/:supportChatId
 *
 * IMPORTANT: We do a server-side access check before loading any chat.
 *
 *   GET /chats/open-check/?kind=event&event_id=123
 *   GET /chats/open-check/?kind=direct&code=ABC
 *
 * It should return:
 *   - 200 (or 204) if access is allowed
 *   - 403 if access is denied
 */
export default function ChatOpen() {
  const { t } = useTranslation("menu");
  const { eventId, openBy, value, groupChatId, supportChatId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routeSearch = String(location?.search || "");
  const routePeer = location?.state?.peer || null;
  const routeEventContext = location?.state?.eventContext || null;

  // Desktop breakpoint (used for desktop-specific loading skeleton)
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024
  );

  useLayoutEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.add("chats-workspace-wide");
    return () => {
      document.body.classList.remove("chats-workspace-wide");
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia("(min-width: 1024px)");
    const apply = () => setIsDesktop(!!mq.matches);

    apply();

    try {
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    } catch {
      // Safari fallback
      mq.addListener(apply);
      return () => mq.removeListener(apply);
    }
  }, []);

  const normalizeCode = (v) => String(v || "").trim().toUpperCase();

  const kind = useMemo(() => {
    if (eventId) return "event";
    if (groupChatId) return "group";
    if (supportChatId) return "support";
    if (openBy && value) return "direct";

    // Support legacy/query navigation: /menu/chats/direct?code=ABC or ?chat_id=123
    try {
      const qs = new URLSearchParams(routeSearch);
      const qCode = (qs.get("code") || "").trim();
      const qChatId = (qs.get("chat_id") || qs.get("id") || "").trim();
      if (qCode || qChatId) return "direct";
    } catch {
      // ignore
    }

    return null;
  }, [eventId, groupChatId, openBy, routeSearch, supportChatId, value]);

  const [chat, setChat] = useState(null);
  const [chatReloadToken, setChatReloadToken] = useState(0);
  const [loading, setLoading] = useState(true);
  const [chatContentReady, setChatContentReady] = useState(false);
  const [chatLoadError, setChatLoadError] = useState(false);
  const [chatRetryKey, setChatRetryKey] = useState(0);
  const [isLeaving, setIsLeaving] = useState(false);
  const [returnChatsBackdrop, setReturnChatsBackdrop] = useState(null);
  const loadingInputSlotActiveRef = useRef(false);
  const backNavigationTimerRef = useRef(null);
  const loadingShellVisible = loading || Boolean(chat && !chatContentReady);

  const handleChatInitialReady = useCallback(() => {
    setChatLoadError(false);
    setChatContentReady(true);
  }, []);

  const handleChatInitialError = useCallback(() => {
    setChatLoadError(true);
    setChatContentReady(true);
  }, []);

  const retryChatMessages = useCallback(() => {
    setChatLoadError(false);
    setChatContentReady(false);
    setChatRetryKey((current) => current + 1);
  }, []);

  const closeLoadingInputSlot = useCallback(() => {
    try {
      window.dispatchEvent(new CustomEvent("vg-chat-input-close"));
    } catch {
      // ignore
    }
  }, []);

  const openLoadingInputSlot = useCallback(() => {
    try {
      window.dispatchEvent(
        new CustomEvent("vg-chat-input-open", {
          detail: {
            props: {
              t: loadingInputT,
              inputValue: "",
              sending: false,
              pendingImages: [],
              unreadCount: 0,
              typingUsers: [],
              showTypingIndicator: false,
              overlayDimmed: false,
              primaryCtaLabel: "",
              onPrimaryCtaClick: null,
              allowAttachments: false,
              canSendMessages: false,
              sendDisabledMessage: "",
              leftActionButtons: [],
              onInputChange: () => {},
              onInputKeyDown: () => {},
              onSend: () => {},
              onPickImages: () => {},
              onRemoveImage: () => {},
              onCancelContext: () => {},
              onOverlayDismiss: () => {},
              onJumpToUnread: () => {},
            },
            no_auto_expand: true,
            source: "chat-loading",
          },
        })
      );
      loadingInputSlotActiveRef.current = true;
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (isDesktop) {
      if (loadingInputSlotActiveRef.current) {
        closeLoadingInputSlot();
        loadingInputSlotActiveRef.current = false;
      }
      return;
    }

    if (isLeaving) {
      if (loadingInputSlotActiveRef.current) {
        closeLoadingInputSlot();
        loadingInputSlotActiveRef.current = false;
      }
      return;
    }

    if (loadingShellVisible) {
      if (!loadingInputSlotActiveRef.current) {
        openLoadingInputSlot();
      }
      return;
    }

    if (!chat && loadingInputSlotActiveRef.current) {
      closeLoadingInputSlot();
      loadingInputSlotActiveRef.current = false;
      return;
    }

    if (chat) {
      // Keep the slot mounted for one frame; the real Chat component updates it
      // with live handlers immediately after mount, avoiding a bottom-nav flash.
      loadingInputSlotActiveRef.current = false;
    }
  }, [chat, closeLoadingInputSlot, isDesktop, isLeaving, loadingShellVisible, openLoadingInputSlot]);

  useEffect(
    () => () => {
      if (backNavigationTimerRef.current) {
        try {
          window.clearTimeout(backNavigationTimerRef.current);
        } catch {
          // ignore
        }
        backNavigationTimerRef.current = null;
      }
      // ChatOpen owns the temporary loading slot. Close it unconditionally on
      // route exit: once the real chat mounts the ref is deliberately cleared,
      // but a fast toast-driven navigation can happen before the child cleanup.
      closeLoadingInputSlot();
      loadingInputSlotActiveRef.current = false;
    },
    [closeLoadingInputSlot]
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setChatContentReady(false);
        setChatLoadError(false);
        setChat(null);

        if (!kind) {
          throw new Error("missing_kind");
        }

        // ---- direct: strict params (no guessing) ----
        let directOpenBy = null;
        let directChatId = null;
        let directCode = null;

        if (kind === "direct") {
          let ob = String(openBy || "").trim().toLowerCase();
          let v = String(value || "").trim();

          // Allow opening via query string (legacy): /menu/chats/direct?code=ABC or ?chat_id=123
          if (!ob || !v) {
            try {
              const qs = new URLSearchParams(routeSearch);
              const qChatId = (qs.get("chat_id") || qs.get("id") || "").trim();
              const qCode = (qs.get("code") || "").trim();

              if (qChatId) {
                ob = "id";
                v = qChatId;
              } else if (qCode) {
                ob = "code";
                v = qCode;
              }
            } catch {
              // ignore
            }
          }

          if (ob === "id") {
            if (!/^\d+$/.test(v)) throw new Error("invalid_direct_id");
            directOpenBy = "id";
            directChatId = Number(v);
          } else if (ob === "code") {
            const c = normalizeCode(v);
            if (!/^[A-Z0-9]{3}$/.test(c)) throw new Error("invalid_direct_code");
            directOpenBy = "code";
            directCode = c;
          } else {
            throw new Error("invalid_direct_openby");
          }
        }

        // 1) Access check (server validates membership / vault-open condition)
        let accessPayload = {};
        try {
          const stateEventContext = routeEventContext;
          const stateEventId =
            stateEventContext?.eventId ||
            stateEventContext?.event_id ||
            stateEventContext?.id ||
            null;
          const params =
            kind === "event"
              ? { kind, event_id: String(eventId) }
              : kind === "group"
              ? { kind, chat_id: String(groupChatId) }
              : kind === "support"
              ? { kind, chat_id: String(supportChatId) }
              : directOpenBy === "id"
              ? { kind, chat_id: String(directChatId) }
              : {
                  kind,
                  code: String(directCode),
                  ...(stateEventId ? { event_id: String(stateEventId) } : {}),
                };

          const resp = await getChatResource("/chats/open-check/", { params });
          accessPayload = resp?.data || {};
        } catch (err) {
          const status = err?.response?.status;
          if (kind === "event" && status === 403) {
            try {
              const { data } = await api.get("/events/professional-meetup-invitations/");
              const invitation = (Array.isArray(data?.results) ? data.results : []).find(
                (item) => String(item.event_id) === String(eventId) && !item.viewer_joined
              );
              if (invitation) {
                if (!cancelled) setChat({ kind: "meetup_invitation", invitation });
                if (invitation.is_unread) {
                  void api.post(`/events/professional-meetup-invitations/${invitation.id}/read/`).catch(() => {});
                }
                return;
              }
            } catch {
              // Keep the regular denied-chat behavior below.
            }
          }
          const message =
            status === 403
              ? "Access to the chat denied."
              : "Can't open this chat right now.";

          if (!cancelled) {
            if (typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent("global-error", {
                  detail: { message, status: status ?? 0 },
                })
              );
            }
            navigate(CHAT_LIST_PATH, {
              replace: true,
              state: { openChatTab: getChatListTabForKind(kind) },
            });
          }
          return;
        }

        // 2) Load chat meta
        if (kind === "event") {
          const [chatMetaResult, eventDetailsResult] = await Promise.allSettled([
            getChatResource(`/chats/${encodeURIComponent(String(eventId))}/internal-chat/`),
            getChatResource(`/events/details/${encodeURIComponent(String(eventId))}/`),
          ]);

          if (chatMetaResult.status !== "fulfilled") {
            throw chatMetaResult.reason || new Error("chat_meta_failed");
          }

          const found = chatMetaResult.value?.data?.chat || null;
          if (!found) throw new Error("chat_not_found");

          const eventDetails =
            eventDetailsResult.status === "fulfilled"
              ? normalizeEventDetailsPayload(eventDetailsResult.value?.data)
              : {};
          const mergedEvent = {
            ...(found?.event || {}),
            ...eventDetails,
          };
          const eventName =
            String(
              eventDetails?.name ||
                eventDetails?.title ||
                found?.event?.name ||
                found?.event_name ||
                found?.event_title ||
                found?.name ||
                ""
            ).trim();
          const eventDateTime =
            eventDetails?.date_time ||
            eventDetails?.datetime ||
            found?.event?.date_time ||
            found?.date_time ||
            null;

          if (!cancelled) {
            setChat({
              ...found,
              kind: "event",
              chat_id: found.chat_id ?? found.id ?? null,
              event_id: Number(eventId) || eventId,
              name: eventName || found?.name || "",
              date_time: eventDateTime,
              event: {
                ...mergedEvent,
                id: eventDetails?.id ?? (Number(eventId) || eventId),
                name: eventName || mergedEvent?.name || found?.name || "",
                date_time: eventDateTime || mergedEvent?.date_time || null,
              },
            });
          }
          return;
        }

        if (kind === "group") {
          const res = await getChatResource(
            `/chats/groups/${encodeURIComponent(String(groupChatId))}/`
          );
          const found = res?.data?.chat || null;
          if (!found) throw new Error("chat_not_found");

          if (!cancelled) {
            setChat({
              ...found,
              kind: "group",
              chat_id: found.chat_id ?? found.id ?? Number(groupChatId),
            });
          }
          return;
        }

        if (kind === "support") {
          const res = await getChatResource(
            `/chats/support/${encodeURIComponent(String(supportChatId))}/`
          );
          const found = res?.data?.chat || null;
          if (!found) throw new Error("chat_not_found");

          if (!cancelled) {
            setChat({
              ...found,
              kind: "support",
              chat_id: found.chat_id ?? found.id ?? Number(supportChatId),
            });
          }
          return;
        }

        // ---------- kind === "direct" ----------
        // We do NOT create a DB chat on navigation.
        // We only validate access here. The DM chat is created on the first message send.

        // Backend returns `code` and/or `chat_id` (for existing DM), plus `peer`.
        const peerFromState = routePeer;
        const peerFromAccess = accessPayload?.peer || null;

        const rawPeer = peerFromAccess || peerFromState || {};

        const isDeletedPeer = isDeletedAccountUser(rawPeer);
        const peerCode = normalizeCode(accessPayload?.code || rawPeer?.code || directCode);
        let existingChatId = accessPayload?.chat_id ?? accessPayload?.direct_chat_id ?? null;

        // IMPORTANT:
        // Do NOT create a DM chat on navigation.
        // If access-check didn't return an existing chat_id, we keep this chat VIRTUAL.
        // The real DB chat will be created ONLY when the first message is sent.
        // (DirectChat will call /chats/direct/open-or-create/ on first send, then canonicalize URL to /id/<id>.)
        //
        // This prevents accumulating empty chats when users just click “Send a message” and leave.

        const normalizedPeer = {
          id: rawPeer?.id ?? null,
          username: rawPeer?.username ?? null,
          code: isDeletedPeer ? null : peerCode || null,
          level: isDeletedPeer ? null : rawPeer?.level ?? null,
          coins: isDeletedPeer ? null : rawPeer?.coins ?? null,
          diamonds: isDeletedPeer ? null : rawPeer?.diamonds ?? null,
          avatar: isDeletedPeer ? null : rawPeer?.avatar ?? null,
          avatar_custom_url: isDeletedPeer ? null : rawPeer?.avatar_custom_url ?? null,
          avatar_url: isDeletedPeer ? null : rawPeer?.avatar_url ?? null,
          last_seen_at: rawPeer?.last_seen_at ?? rawPeer?.lastSeenAt ?? null,
          is_online: !isDeletedPeer && Boolean(rawPeer?.is_online ?? rawPeer?.isOnline),
          presence_expires_at: isDeletedPeer ? null : rawPeer?.presence_expires_at ?? null,
          has_premium_subscription: !isDeletedPeer && Boolean(
            rawPeer?.has_premium_subscription ?? rawPeer?.is_premium ?? rawPeer?.isPremium
          ),
          is_deleted_account: isDeletedPeer,
        };

        const accessEventContext = accessPayload?.event_context || null;
        const routeEventId = Number(
          routeEventContext?.eventId || routeEventContext?.event_id || routeEventContext?.id || 0
        );
        const accessEventId = Number(
          accessEventContext?.eventId || accessEventContext?.event_id || accessEventContext?.id || 0
        );
        const routeIconImageUrl = routeEventId && routeEventId === accessEventId
          ? routeEventContext?.icon_image_url || routeEventContext?.iconImageUrl || null
          : null;

        if (!cancelled) {
          setChat({
            kind: "direct",
            open_by: existingChatId ? "id" : directOpenBy,
            chat_id: existingChatId,
            direct_chat_id: existingChatId,
            is_virtual: !existingChatId,
            peer_code: peerCode,
            peer: normalizedPeer,
            eventContext: accessEventContext
              ? {
                  ...accessEventContext,
                  icon_image_url: accessEventContext.icon_image_url || routeIconImageUrl,
                }
              : routeEventContext || null,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setChat(null);
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("global-error", {
                detail: {
                  message: "Accessing the chat denied",
                  status: 0,
                },
              })
            );
          }
          navigate(CHAT_LIST_PATH, {
            replace: true,
            state: { openChatTab: getChatListTabForKind(kind) },
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (kind) {
      load();
    } else {
      setLoading(false);
      setChat(null);
    }

    return () => {
      cancelled = true;
    };
  }, [
    kind,
    eventId,
    groupChatId,
    navigate,
    openBy,
    routeEventContext,
    routePeer,
    routeSearch,
    supportChatId,
    value,
    chatReloadToken,
  ]);

  useEffect(() => {
    if (!loading && !chat) {
      navigate(CHAT_LIST_PATH, {
        replace: true,
        state: { openChatTab: getChatListTabForKind(kind) },
      });
    }
  }, [loading, chat, kind, navigate]);

  const returnToChatsState = useMemo(
    () => resolveReturnToChatsState({ kind, location }),
    [kind, location]
  );

  const performBackNavigation = useCallback(() => {
    navigate(
      CHAT_LIST_PATH,
      buildChatListNavigationOptions({
        tab: returnToChatsState.tab,
        scrollTop: returnToChatsState.scrollTop,
      })
    );
  }, [navigate, returnToChatsState]);

  const handleBackFromChat = useCallback(() => {
    if (isLeaving) return;
    // The route remains mounted during its exit animation, but the shared
    // composer must disappear at the moment the user leaves the chat.
    closeLoadingInputSlot();
    loadingInputSlotActiveRef.current = false;
    if (returnToChatsState.showBackdrop) {
      setReturnChatsBackdrop({
        tab: returnToChatsState.tab,
        scrollTop: returnToChatsState.scrollTop,
      });
    }
    setIsLeaving(true);

    backNavigationTimerRef.current = window.setTimeout(() => {
      backNavigationTimerRef.current = null;
      performBackNavigation();
    }, CHAT_ROUTE_TRANSITION_MS);
  }, [closeLoadingInputSlot, isLeaving, performBackNavigation, returnToChatsState]);
  useNativeSwipeBack(handleBackFromChat, {
    enabled: !isDesktop && !isLeaving,
    priority: 20,
  });

  const routeKey = `chat-route-${kind || "chat"}`;
  const chatContent = !chat ? null : (
    <div className="flex h-full min-h-0 flex-col">
      {chat.kind === "meetup_invitation" ? (
        <ProfessionalMeetupInviteChat
          invitation={chat.invitation}
          deferInputUntilReady={isLeaving || !chatContentReady}
          onBack={handleBackFromChat}
          onInitialReady={handleChatInitialReady}
          onJoined={() => setChatReloadToken((current) => current + 1)}
        />
      ) : kind === "direct" ? (
        <DirectChat
          key={chatRetryKey}
          chat={chat}
          onBack={handleBackFromChat}
          onInitialReady={handleChatInitialReady}
          onInitialError={handleChatInitialError}
          deferInputUntilReady={isLeaving || !chatContentReady}
        />
      ) : (
        <Chat
          key={chatRetryKey}
          chat={chat}
          onBack={handleBackFromChat}
          onInitialReady={handleChatInitialReady}
          onInitialError={handleChatInitialError}
          deferInputUntilReady={isLeaving || !chatContentReady}
        />
      )}
    </div>
  );
  const hasRouteContent = loadingShellVisible || Boolean(chatContent);

  return (
    <div className="relative h-full min-h-0 overflow-hidden" style={AUTH_GRADIENT_STYLE}>
      {returnChatsBackdrop ? (
        <ChatsModal
          visible
          onClose={() => {}}
          asPage={isDesktop}
          initialTabKey={returnChatsBackdrop.tab}
          initialScrollTop={returnChatsBackdrop.scrollTop}
          transitionBackdrop
        />
      ) : null}
      <AnimatePresence mode="sync" initial>
        {hasRouteContent ? (
          <motion.div
            key={routeKey}
            className="absolute inset-0 z-30 h-full min-h-0"
            style={{ transformOrigin: "50% 72%" }}
            variants={chatRouteVariants}
            initial="initial"
            animate={isLeaving ? "leaving" : "animate"}
            exit="exit"
          >
            <div className="relative h-full min-h-0">
              {chatContent}
              {chatLoadError ? (
                <div className="pointer-events-none absolute inset-x-0 top-1/2 z-30 flex -translate-y-1/2 justify-center px-6">
                  <div role="alert" className="pointer-events-auto max-w-sm rounded-2xl border border-white/15 bg-[#17233D] px-6 py-5 text-center shadow-xl">
                    <p className="text-sm font-semibold text-white">
                      {t("chats.list.messages_load_failed")}
                    </p>
                    <button
                      type="button"
                      onClick={retryChatMessages}
                      className="mt-4 min-h-11 rounded-full bg-[#3E6FFB] px-6 text-sm font-semibold text-white"
                    >
                      {t("chats.list.retry")}
                    </button>
                  </div>
                </div>
              ) : null}
              <AnimatePresence initial={false}>
                {loadingShellVisible ? (
                  <motion.div
                    key="chat-loading-shell"
                    className="absolute inset-0 z-40 h-full min-h-0"
                    variants={chatContentVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  >
                    <ChatLoadingShell
                      kind={kind}
                      isDesktop={isDesktop}
                      onBack={handleBackFromChat}
                    />
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
